/**
 * Equipment type → casing inspection type.
 *
 * **The shared source is `fieldiq-api/src/lib/equipment-inspection-type.ts`.**
 * The rules below are a byte-for-byte copy of it, differing only in the header.
 * Two consumers, one vocabulary: this file picks a tech's checklist on the
 * inspection form, and the API's `processInspectionReminder` uses the same
 * mapping to decide whether a unit can be inspected at all — a unit whose type
 * maps to nothing is no longer flagged for inspection. A rule changed on one
 * side and not the other gives two answers to one question, so
 * `equipmentInspectionType.test.ts` carries the same case table as the API's
 * suite and both must be edited together.
 *
 * Equipment types are tenant-authored free text: `EquipmentScreen` lets an admin
 * type a new name and POSTs it to /equipment-types, so there is no enum to
 * switch on. The mapping below therefore matches on normalised names rather
 * than exact strings, and returns null for anything it does not recognise —
 * the inspection form falls back to its manual type dropdown in that case.
 *
 * Deliberately conservative. Here a wrong match locks the tech into the wrong
 * checklist; in the API's reminder a wrong match flags a unit no checklist
 * fits. So "Backup Tong" and "Spider" get no inspection type, while "Power
 * Tongs", "Integral Tong" and "Tongs" get one.
 */

/** The six inspection types EquipmentInspectionForm can render. */
export const INSPECTION_TYPES = [
  'Tong Truck',
  'Power Tong',
  'CRTi',
  'Elevators',
  'Slip Elevator L1',
  'Rotary Slips L1',
] as const;

export type InspectionType = typeof INSPECTION_TYPES[number];

/** Dropdown labels. The stored value is the key, not this text. */
export const INSPECTION_TYPE_LABELS: Record<InspectionType, string> = {
  'Tong Truck': 'Tong Truck',
  'Power Tong': 'Power Tong',
  'CRTi': 'CRTi - Casing Running Tool',
  'Elevators': 'Elevators - Conventional Casing',
  'Slip Elevator L1': 'Slip Elevator — L1 Pre-Use',
  'Rotary Slips L1': 'Rotary Slips — L1 Pre-Use',
};

/** Lowercase, collapse every run of non-alphanumerics to a single space. */
function normalize(raw: string): string {
  return raw.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

/**
 * Ordered — first match wins, and the order is load bearing. "Slip Elevator"
 * has to be tested before any bare slip rule, "Rotary Slips" before it too,
 * and "Tong Truck" before "Power Tong", or the more general rule below
 * would swallow the more specific type above it.
 */
const RULES: { match: (n: string) => boolean; type: InspectionType }[] = [
  { match: n => n.includes('tong truck'), type: 'Tong Truck' },
  { match: n => n.includes('rotary slip'), type: 'Rotary Slips L1' },
  { match: n => n.includes('slip elevator'), type: 'Slip Elevator L1' },
  {
    match: n => /\bcrt\b/.test(n) || /\bcrti\b/.test(n) || n.includes('casing running tool'),
    type: 'CRTi',
  },
  // Qualified tongs only. A bare "tong" inside "backup tong" or "stabbing tong"
  // is a different tool and gets the manual dropdown. The qualifiers are
  // enumerated rather than matched loosely for exactly that reason.
  {
    match: n =>
      n.includes('power tong') ||
      n.includes('integral tong') ||
      n.includes('casing tong') ||
      n === 'tong' ||
      n === 'tongs',
    type: 'Power Tong',
  },
  { match: n => n.includes('elevator'), type: 'Elevators' },
  // Bare slips, below "slip elevator" and "rotary slip" so neither is caught
  // here. In casing, unqualified "Slips" are the rotary-table slips and take
  // the rotary_slips_l1 checklist — the tenants that submit against those units
  // already file them as `rotary_slips_l1`.
  { match: n => n === 'slip' || n === 'slips', type: 'Rotary Slips L1' },
];

/**
 * Returns the inspection type an equipment type should open, or null when the
 * type is unrecognised — on the form the tech picks from the dropdown, and in
 * the inspection reminder the unit is not flagged on mapping grounds.
 */
export function inspectionTypeForEquipmentType(
  equipmentType: string | null | undefined,
): InspectionType | null {
  if (!equipmentType) return null;
  const n = normalize(equipmentType);
  if (!n) return null;
  return RULES.find(r => r.match(n))?.type ?? null;
}
